/**
 * EventPlaybookService
 * Events hub Stage 2 playbooks: reusable lists of the tasks, projects (from a template), meetings
 * and bookings an event needs, with timing relative to the event and an assignee strategy.
 * Route: events/api/events/playbooks (reads: Event.View; writes: Admin role OR Event.Playbook.Manage)
 */
import { BaseService } from '../base/BaseService';
import { unwrapEventsResponse as unwrap } from './unwrapEventsResponse';
export class EventPlaybookService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? EventPlaybookService.DEFAULT_SERVICE_PATH,
            basePath: EventPlaybookService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** GET api/events/playbooks (Event.View). Every playbook that is not deleted, with its items. */
    async list() {
        const res = await this.listRaw();
        return res.data ?? [];
    }
    listRaw() {
        return this.http.get(this.buildUrl());
    }
    /** GET api/events/playbooks/{id} (Event.View). */
    async get(playbookId) {
        return unwrap(await this.getRaw(playbookId), `Playbook ${playbookId} not found`);
    }
    getRaw(playbookId) {
        return this.http.get(this.buildUrl(String(playbookId)));
    }
    /** POST api/events/playbooks (Event.Playbook.Manage). */
    async create(request) {
        return unwrap(await this.createRaw(request), 'Could not create the playbook');
    }
    createRaw(request) {
        return this.http.post(this.buildUrl(), request);
    }
    /** PUT api/events/playbooks/{id} (Event.Playbook.Manage). Replaces the items. Existing plans keep their copy. */
    async update(playbookId, request) {
        return unwrap(await this.updateRaw(playbookId, request), `Could not update playbook ${playbookId}`);
    }
    updateRaw(playbookId, request) {
        return this.http.put(this.buildUrl(String(playbookId)), request);
    }
    /** DELETE api/events/playbooks/{id} (Event.Playbook.Manage). Soft delete; plans keep their copy. */
    async delete(playbookId) {
        const res = await this.deleteRaw(playbookId);
        if (res.success === false)
            throw new Error(res.message || `Could not delete playbook ${playbookId}`);
        return true;
    }
    deleteRaw(playbookId) {
        return this.http.delete(this.buildUrl(String(playbookId)));
    }
    /** GET api/events/playbooks/suggestions?eventId (Event.View). Playbooks suggested for one event. */
    async suggestions(eventId) {
        const res = await this.suggestionsRaw(eventId);
        return res.data ?? [];
    }
    suggestionsRaw(eventId) {
        return this.http.get(this.buildUrlWithParams('suggestions', { eventId }));
    }
}
EventPlaybookService.DEFAULT_SERVICE_PATH = 'events';
EventPlaybookService.BASE_PATH = 'api/events/playbooks';
//# sourceMappingURL=EventPlaybookService.js.map